// appsettings carries ONLY origin-specific runtime config (auth client, API
// URLs, telemetry). The remote catalog is code-defined — see remotes.config.ts.
export interface AppSettings {
  authAuthority: string;
  authClientId: string;
  authScope: string;
  apiBaseUrl?: string;
  /**
   * User-management API base, the rbac permissions source for
   * reference-token clients. Omit for JWT-token clients whose access token
   * carries the Perm claim.
   */
  userManagementApiUrl?: string;
  /**
   * Application Insights wiring. connectionString is committed empty and
   * substituted at deploy time; empty means telemetry is skipped (local dev).
   */
  telemetry: {
    connectionString: string;
    enabled: boolean;
  };
  /**
   * Per-environment remote entry-URL overrides, keyed by remote id from the
   * code-defined catalog (remotes.config.ts). URLs are the only env-specific
   * piece of the catalog — everything else is reviewed code.
   *
   * Replaces the old remotes[] + remoteEntryOverrides pair: this map carries
   * the same Vault-injectable dotted-key semantics (scalars keyed by catalog
   * id) that remoteEntryOverrides existed for, so collapsing the two loses no
   * capability.
   */
  remoteEntries?: Record<string, string>;
}

const DEFAULT_APP_SETTINGS_URL = "/appsettings.json";
const LOCAL_APP_SETTINGS_URL = "/appsettings.local.json";
const appSettingsCache = new Map<string, unknown>();

async function fetchSettings<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
    },
  });

  if (!response.ok) {
    throw new Error(`[app-settings] Failed to load ${url} (${response.status})`);
  }

  return (await response.json()) as T;
}

export async function loadAppSettings<T = AppSettings>(options?: {
  configUrl?: string;
  forceReload?: boolean;
}): Promise<T> {
  const configUrl = options?.configUrl || DEFAULT_APP_SETTINGS_URL;

  if (!options?.forceReload && appSettingsCache.has(configUrl)) {
    return appSettingsCache.get(configUrl) as T;
  }

  // Local override is dev-only by construction: import.meta.env.DEV is a
  // build-time constant, so production bundles tree-shake this branch
  // entirely and never attempt to fetch .local — no runtime env sniffing,
  // no accidental pickup on a deployed origin. A 404/fetch error here is the
  // normal case (no override present) and falls through silently.
  if (import.meta.env.DEV && configUrl === DEFAULT_APP_SETTINGS_URL) {
    try {
      const localResponse = await fetch(LOCAL_APP_SETTINGS_URL, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache",
        },
      });
      if (localResponse.ok) {
        const loaded = (await localResponse.json()) as T;
        appSettingsCache.set(configUrl, loaded);
        return loaded;
      }
    } catch {
      // fall through to the committed appsettings.json
    }
  }

  const loaded = await fetchSettings<T>(configUrl);
  appSettingsCache.set(configUrl, loaded);
  return loaded;
}

export function getAppSettings(): AppSettings {
  const appSettings = appSettingsCache.get(DEFAULT_APP_SETTINGS_URL) as AppSettings | undefined;

  if (!appSettings) {
    throw new Error("[app-settings] Settings not loaded. Call loadAppSettings() during bootstrap.");
  }

  return appSettings;
}
